/** Normalise generator config SNOMED code object from the API into a stable client shape */
export const normalizeFetchedCodeRecord = (raw) => {
  if (!raw || typeof raw !== 'object' || raw.code === undefined || raw.code === null) {
    return null;
  }
  const terms = Array.isArray(raw.terms) ? [...raw.terms] : [];
  return {
    code: String(raw.code),
    terms,
    is_qof: !!raw.is_qof,
  };
};

/** Generator map { [conceptId]: Code } from fetch */
export const normalizeCodeMap = (raw) => {
  if (!raw || typeof raw !== 'object') return {};
  const out = {};
  for (const val of Object.values(raw)) {
    const c = normalizeFetchedCodeRecord(val);
    if (c) out[c.code] = c;
  }
  return out;
};

/** First character uppercased; rest unchanged (for persisted preferred terms from SNOMED search). */
const capitalizeFirstLetter = (value) => {
  const s = String(value ?? '');
  if (!s.length) return s;
  return s.charAt(0).toLocaleUpperCase() + s.slice(1);
};

/** Map global SNOMED search result rows to Code for save/display */
export const codeRecordFromSnomedSearchResult = (result) => {
  if (!result) return null;
  const nested = result.code && typeof result.code === 'object' ? result.code : null;
  const id = nested?.code ?? result.code;
  if (id === undefined || id === null || id === '') return null;
  const termsRaw = nested?.terms ?? result.terms;
  const preferred =
    Array.isArray(termsRaw) && termsRaw.length > 0
      ? capitalizeFirstLetter(String(termsRaw[0]))
      : typeof termsRaw === 'string'
        ? capitalizeFirstLetter(termsRaw)
        : '';
  return {
    code: String(id),
    terms: preferred ? [preferred] : [],
    is_qof: !!(nested?.is_qof ?? result.is_qof),
  };
};

export const codesEqualIgnoringTerms = (a, b) => {
  const ca = a?.code ?? (typeof a === 'string' ? a : undefined);
  const cb = b?.code ?? (typeof b === 'string' ? b : undefined);
  if ((ca === undefined || ca === null || ca === '') && (cb === undefined || cb === null || cb === '')) {
    return true;
  }
  if ((ca === undefined || ca === null || ca === '') || (cb === undefined || cb === null || cb === '')) {
    return false;
  }
  return String(ca) === String(cb);
};

export const codeRecordsEqual = (a, b) => {
  if (!a && !b) return true;
  if (!a || !b) return false;
  if (String(a.code) !== String(b.code)) return false;
  if (!!a.is_qof !== !!b.is_qof) return false;
  const termsA = Array.isArray(a.terms) ? a.terms : [];
  const termsB = Array.isArray(b.terms) ? b.terms : [];
  if (termsA.length !== termsB.length) return false;
  return termsA.every((term, index) => term === termsB[index]);
};

export const nextCustomCode = (existingCodesMap) => {
  const keys = Object.keys(existingCodesMap || {});
  let n = 0;
  while (keys.includes(`CUSTOM${n}`)) n++;
  return `CUSTOM${n}`;
};

export const cloneCodeRecord = (record) => (
  record ? { ...record, terms: [...(record.terms || [])] } : null
);

export const mergeCodeMapWithDelta = (baseMap, delta) => {
  const next = { ...(baseMap || {}) };
  Object.entries(delta || {}).forEach(([id, record]) => {
    if (record === null || record === undefined) {
      delete next[id];
    } else {
      next[id] = record;
    }
  });
  return next;
};
